/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';
import React, { FC, ReactNode, useRef } from 'react';
import styled from '@emotion/styled/macro';
import { useParams } from 'react-router-dom';

import { Flex, Box, FlexProps, Grid } from 'components/styled';
import Text from 'components/styled/Text';
import { default as TitleTextOrg } from 'components/styled/TitleText';
import {
	Wrapper,
	ResponsiveWrapper,
	ResponsivePadder,
} from 'components/styled/Wrapper';
import Button, {
	NavButton,
	NavHrefButton,
	NavLinkButton,
} from 'components/styled/Button';
import ScrollToTopButton from 'components/floatingactions/ScrollToTopButton';
import { ListItem, UnorderedList } from 'components/styled/List';
import Paper from 'components/styled/Paper';
import ModalDialog from 'components/modal';
import { HrefLink } from 'components/styled/Link';

import { ReactComponent as PortalLogoSVG } from 'assets/plzen/portal-logo.svg';

import { usePageTitle } from 'hooks/seoHooks';
import useBreakpoint from 'hooks/useBreakpoint';

import { useLocalizedMultistring } from 'utils/localization';

import CountupInfo from '../CountupInfo';
import { useScrollToSection } from '../_utils';

import homeBg from './homebg.jpg';
import studentsBlue from './studentsblue.jpg';
import companyBlue from './companyblue.jpg';
import workingBlue from './workingblue.jpg';
import topViewBlue from './topviewblue.jpg';
import homeCompany from './homeCompany.jpg';
import homeAlumni from './homeAlumni.jpg';
import homeStudents from './homeStudents.jpg';

const TitleText = styled(TitleTextOrg)`
	text-transform: uppercase;
	color: ${p => p.theme.colors.primary};
	margin-left: 0px !important;
`;

type Props = {
	imageSrc: string;
	children?: ReactNode;
} & FlexProps;

const BlueBackground: React.FC<Props> = ({ imageSrc, children, ...props }) => (
	<Flex
		flex={1}
		alignItems="center"
		justifyContent="center"
		p={[3, 4]}
		css={(theme: Theme) => css`
			background-image: url(${imageSrc});
			background-size: cover;
			background-position: top center;
			position: relative;
			color: #fff;
			z-index: -1;
			&::before {
				content: '';
				background-color: ${theme.colors.primary};
				opacity: 0.75;
				position: absolute;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
				z-index: -1;
			}
		`}
		{...props}
	>
		{children}
	</Flex>
);

const PlzenHomepage: FC = () => {
	const localize = useLocalizedMultistring();
	// Page title
	usePageTitle(localize({ CZECH: 'Vítejte', ENGLISH: 'Welcome' }));

	const isMobile = useBreakpoint(0);

	const aboutSectionRef = useRef<HTMLDivElement>(null);
	const companySectionRef = useRef<HTMLDivElement>(null);
	const studentSectionRef = useRef<HTMLDivElement>(null);
	const alumnusSectionRef = useRef<HTMLDivElement>(null);
	const { anchor } = useParams<{ anchor: string }>();

	let section: React.RefObject<HTMLDivElement> | null = null;
	switch (anchor) {
		case 'about':
			section = aboutSectionRef;
			break;
		case 'student':
			section = studentSectionRef;
			break;
		case 'alumnus':
			section = alumnusSectionRef;
			break;
		case 'company':
			section = companySectionRef;
			break;
	}

	useScrollToSection(section);
	return (
		<Wrapper>
			<Flex
				alignItems="center"
				css={css`
					background-image: url(${homeBg});
					background-size: cover;
					background-position: center;
					background-repeat: no-repeat;
					width: 100%;
					min-height: 400px;
					max-height: 400px;
				`}
			>
				<ResponsivePadder>
					<Box bg="primary" width="fit-content" p={4}>
						<Flex>
							<Flex
								color="white"
								css={(theme: Theme) => css`
									width: 150px;
									height: 30px;
									margin-top: -72px;
									margin-left: -13px;
									@media (max-width: ${theme.breakpoints[0]}) {
										width: 130px;
										height: 30px;
										margin-top: -78px;
										margin-left: -13px;
									}
								`}
							>
								<PortalLogoSVG />
							</Flex>

							<Text
								as={isMobile ? 'h2' : 'h1'}
								color="white"
								fontWeight={600}
								m={0}
								p={0}
							>
								{localize({
									CZECH: 'Kariérní portál',
									ENGLISH: 'Career Portal of',
								})}
							</Text>
						</Flex>
						<Text
							as={isMobile ? 'h2' : 'h1'}
							color="white"
							fontWeight={600}
							m={0}
							p={0}
						>
							{localize({
								CZECH: 'Západočeské univerzity v Plzni',
								ENGLISH: 'University of West Bohemia',
							})}
						</Text>
					</Box>
				</ResponsivePadder>
			</Flex>

			<ResponsiveWrapper
				pt={4}
				display="flex"
				justifyContent="center"
				ref={aboutSectionRef}
			>
				<TitleText>
					{localize({
						CZECH: 'Co unijobs nabízí?',
						ENGLISH: 'What does UniJobs offer?',
					})}
				</TitleText>
			</ResponsiveWrapper>

			<ResponsiveWrapper flexDirection={['column', 'row']}>
				<BlueBackground minHeight={[250, 350]} imageSrc={workingBlue}>
					<Box color="white" textAlign="center">
						<Text
							fontSize="lg"
							fontWeight="bold"
							css={css`
								text-transform: uppercase;
							`}
						>
							{localize({
								CZECH: 'Studentům',
								ENGLISH: 'TO STUDENTS',
							})}
						</Text>
						<Text>
							{localize({
								CZECH:
									'portál pomáhá najít relevantní pracovní pozici, stáž či brigádu v oboru. Zprostředkovává kontakt studentů s firmami vstřícnými ke spolupráci na studentských závěrečných pracích.',
								ENGLISH:
									'The Portal helps to find a relevant job position, internship, work experience or part-time job in the field. It mediates the contact between students and companies willing to co-work on students’ final theses.',
							})}
						</Text>
					</Box>
				</BlueBackground>
				<BlueBackground minHeight={[250, 350]} imageSrc={companyBlue}>
					<Box color="white" textAlign="center">
						<Text
							fontSize="lg"
							fontWeight="bold"
							css={css`
								text-transform: uppercase;
							`}
						>
							{localize({
								CZECH: 'Absolventům',
								ENGLISH: 'TO ALUMNI',
							})}
						</Text>
						<Text>
							{localize({
								CZECH:
									'do tří let po ukončení studia portál nabízí relevantní pracovní pozice jak na plný, tak na částečný úvazek. Lze zde také najít pracovní nabídky pro freelancery.',
								ENGLISH:
									'Within three years from graduation, the Portal offers relevant full-time and part-time jobs. It is possible to find job offers for freelancers here.',
							})}
						</Text>
					</Box>
				</BlueBackground>
				<BlueBackground minHeight={350} imageSrc={studentsBlue}>
					<Box color="white" textAlign="center">
						<Text
							fontSize="lg"
							fontWeight="bold"
							css={css`
								text-transform: uppercase;
							`}
						>
							{localize({
								CZECH: 'Zaměstnavatelům',
								ENGLISH: 'TO EMPLOYERS',
							})}
						</Text>
						<Text>
							{localize({
								CZECH:
									'portál nabízí možnost oslovit studenty Západočeské univerzity v Plzni. Umožňuje inzerci pracovních nabídek či jiných příležitostí ke spolupráci firmy se studenty a vyhledávání studentů dle zadaných kritérií.',
								ENGLISH:
									'The Portal offers the possibility to reach out students of the UWB in Pilsen. It allows to make advertisement of job offers or other opportunities to the company´s cooperation with students and the search of students according to specified criteria.',
							})}
						</Text>
					</Box>
				</BlueBackground>
			</ResponsiveWrapper>

			<ResponsiveWrapper pt={4}>
				<Box bg="primaryLight" color="primary" fontWeight="bold" fontSize="md">
					<CountupInfo hideInteractions />
				</Box>
			</ResponsiveWrapper>

			<ResponsiveWrapper
				pt={[0, 2, 3, 4]}
				flexDirection={['column', 'row']}
				ref={studentSectionRef}
			>
				<Box width={[1, 1 / 2]}>
					<TitleText width="fit-content" p={0}>
						{localize({
							CZECH: 'Pro studenty',
							ENGLISH: 'Student',
						})}
					</TitleText>
					<Text>
						{localize({
							CZECH:
								'Jste student nebo studentka a kariéra je pro Vás něco vzdáleného? Čím dřív začnete sbírat zkušenosti v oboru, tím lépe. Portál UniJobs je tu přesně pro Vás. Najdete tady desítky příležitostí ke spolupráci se zaměstnavateli a můžete postupně získávat praxi již od prvního ročníku.',
							ENGLISH:
								'Are you a student and a career is something so remote to you? The sooner you start gaining experience in the field, the better. UniJobs Portal is here for you. Here you will find dozens of opportunities for co-working with employers and you can gradually gain experience from the first year.',
						})}
					</Text>
					<Text>
						{localize({
							CZECH:
								'Díky propojení portálu se STAGem je registrace rychlá a jednoduchá. Co v portálu najdete?',
							ENGLISH:
								'Thanks to the connection of the Portal with STAG, the registration is quick and easy. What will you find in the Portal?',
						})}
					</Text>
					<UnorderedList>
						<ListItem>
							{localize({
								CZECH: 'profily zaměstnavatelů',
								ENGLISH: 'Employer´s profiles',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'nabídky práce',
								ENGLISH: 'Job offers',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'nabídky praxí a stáží',
								ENGLISH: 'Intership offers',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'trainee programy',
								ENGLISH: 'Trainee programs',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'odborné brigády (DPP, DPČ)',
								ENGLISH:
									'Professional brigade-works (Contract for work, Agreement on work activity)',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'témata bakalářských a diplomových prací',
								ENGLISH: 'Topics of bachelor´s and master´s theses',
							})}
						</ListItem>
					</UnorderedList>
					<Flex flexWrap="wrap" mt={3} gap={3}>
						<NavLinkButton
							variant="primary"
							to="/login"
							aria-label={localize({
								CZECH: 'Student - Registrujte se zde',
								ENGLISH: 'Student - Register here',
							})}
						>
							{localize({
								CZECH: 'Registrujte se zde',
								ENGLISH: 'Register here',
							})}
						</NavLinkButton>
						<ModalDialog
							aria-label={localize({
								CZECH: 'Student - více informací',
								ENGLISH: 'Student - More infromation',
							})}
							label={localize({
								CZECH: 'Student - Více informací',
								ENGLISH: 'Student - More infromation',
							})}
							control={openModal => (
								<NavButton
									variant="primary"
									onClick={openModal}
									aria-label={localize({
										CZECH: 'Student - Více informací',
										ENGLISH: 'Student - More infromation',
									})}
								>
									{localize({
										CZECH: 'Více informací',
										ENGLISH: 'More infromation',
									})}
								</NavButton>
							)}
						>
							{closeModal => (
								<Paper>
									<Box px={4} py={3}>
										<Text fontWeight="bold" textAlign="center" fontSize={18}>
											{localize({
												CZECH: 'Student - více informací',
												ENGLISH: 'Student - more infromation',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH: 'Registrujte se a vyplňte si svůj profil',
												ENGLISH: 'Register and fill in your profile',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'UniJobs Vám umožní vytvořit si najednou vlastní kariérní profil a životopis, který můžete rychle aktualizovat a máte ho k dispozici efektivně online. Představte se potenciálním zaměstnavatelům prostřednictvím svých znalostí, dovedností i zkušeností. Uveďte rovněž své kariérní preference, které Vám usnadní vyhledávání relevantních nabídek.',
												ENGLISH:
													'UniJobs allows you to create your career profile and CV at once, which you can quickly update and is effectively available online. Introduce yourself to potential employers via your knowledge, skills and experience. Set your other career preferences that will help you to search for relevant offers.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH: 'Prohlížejte nabídky zaměstnavatelů',
												ENGLISH: 'Browse through the employer´s offers',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Nabídky v portálu UniJobs jsou určené právě pro Vás. Pracovní pozice jsou relevantní pro studenty nebo čerstvé	absolventy, proto u nich není vyžadována dlouhá praxe. Již během studia můžete využít nabídek praxí/stáží, trainee programů nebo témat kvalifikačních prací.',
												ENGLISH:
													'The offers in UniJobs Portal are designed just for you. Job positions are relevant for students and fresh graduates, so long-term experience is not needed/ required here. During your studies, you can exploit the internship offers, trainee programs or topics for qualification theses.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH:
													'Kontaktujte zaměstnavatele s relevantní nabídkou přímo pro Vás',
												ENGLISH:
													'Contact the employer with the offers relevant to you',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Portál UniJobs Vám automaticky doporučí odpovídající nabídky zaměstnavatelů na základě Vašich preferencí a dovedností. Máte k dispozici průběžné informace o tom, které požadavky v nabídkách splňujete a kde máte naopak prostor pro svůj další rozvoj. Podporuje Vás v dalším vzdělávání v přímé vazbě na požadavky trhu práce. Zvyšujte své znalosti a dovednosti dle doporučení, rozšíříte si možnost uplatnění po absolvování studia.',
												ENGLISH:
													'UniJobs Portal will automatically recommend you the appropriate job offers from employers based on your preferences and skills. You still have available actual information about which requirements you fulfill in the offers and where you have the space for developing yourself. It supports you in further education in direct connection on job market requirements. Increase your knowledge and skills according to the recommendations, you will extend the possibility of employment after graduation.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH: 'Využívejte efektivně svůj čas',
												ENGLISH: 'Use your time effectively',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'UniJobs Vám ušetří čas při získávání praktických zkušeností i hledání vhodného tématu bakalářské/diplomové práce. Na vybranou nabídku zareagujete jedním kliknutím! Pokud svůj profil kvalitně vyplníte, mohou Vás zaměstnavatele aktivně oslovit s nabídkou, která bude přesně odpovídat Vašim znalostem a představám. Věnujte čas vyplnění svého profilu hned na začátku, ušetříte ho více v budoucnosti.',
												ENGLISH:
													'UniJobs will save you time in gaining practical experience and finding the suitable topic for your bachelor´s/ master´s thesis. You can answer the selected offer with one click! If you fill in your profile well, employers can actively address to you with an offer that will exactly correspond to your knowledge and ideas/ imaginings. Take the time to fill in your profile right at the start, it will save your time in the future.',
											})}
										</Text>
										<Text mt={4}>
											{localize({
												CZECH: 'Potřebujete poradit? Napište nám na e-mail: ',
												ENGLISH: 'Do you need an advice? Write us at e-mail: ',
											})}
											<HrefLink color="primary" href="mailto:career@rek.zcu.cz">
												career@rek.zcu.cz
											</HrefLink>
											.
										</Text>
										<Flex
											justifyContent="space-between"
											alignItems="center"
											mt={4}
										>
											<Button onClick={closeModal} variant="outlined">
												{localize({
													CZECH: 'Zpět',
													ENGLISH: 'Back',
												})}
											</Button>
											<NavButton
												variant="primary"
												onClick={() =>
													window.location.replace('/api/jch/auth/student')
												}
											>
												{localize({
													CZECH: 'Zaregistrovat se',
													ENGLISH: 'Register',
												})}
											</NavButton>
										</Flex>
									</Box>
								</Paper>
							)}
						</ModalDialog>
					</Flex>
				</Box>
				<Flex
					width={[1, 1 / 2]}
					ml={[0, 4]}
					mt={4}
					height={300}
					css={css`
						background-image: url(${homeStudents});
						background-size: cover;
						background-repeat: no-repeat;
						background-position: center;
					`}
				/>
			</ResponsiveWrapper>

			<ResponsiveWrapper
				pt={[0, 2, 3, 4]}
				flexDirection={['column', 'row']}
				ref={alumnusSectionRef}
			>
				<Flex
					order={[1, 0]}
					mt={4}
					width={[1, 1 / 2]}
					height={300}
					css={css`
						background-image: url(${homeAlumni});
						background-size: cover;
						background-repeat: no-repeat;
						background-position: center;
					`}
				/>
				<Box width={[1, 1 / 2]} ml={[0, 4]}>
					<TitleText width="fit-content" p={0}>
						{localize({
							CZECH: 'Pro absolventy',
							ENGLISH: 'Alumni',
						})}
					</TitleText>
					<Text>
						{localize({
							CZECH:
								'Jste čerstvý absolvent/ka s maximálně třemi lety praxe? Portál UniJobs je tu stále i pro Vás. Najdete tady nabídky zaměstnavatelů, kteří hledají juniory. Zaregistrujte se, vyplňte si svůj profil. Zaměstnavatelé absolventům prostřednictvím svých profilů na UniJobs portálu nabízí:',
							ENGLISH:
								'Are you fresh graduate with three years of experience maximum? UniJobs Portal is here for you as well. Here you can find offers from employers looking for Juniors. Just register, fill in your profile. The employers of alumni via their profiles on UniJobs Portal:',
						})}
					</Text>
					<UnorderedList>
						<ListItem>
							{localize({
								CZECH: 'nabídky práce',
								ENGLISH: 'Job offers',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'trainee programy',
								ENGLISH: 'Trainee programs',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'odborné brigády (DPP, DPČ)',
								ENGLISH:
									'Professional work-brigades (Contract for work, Agreement on work activity)',
							})}
						</ListItem>
					</UnorderedList>
					<Text>
						{localize({
							CZECH:
								'Pro registraci jako absolvent stačí propojit Váš účet s registrací v Klubu absolventů ZČU.',
							ENGLISH:
								'For registration as an alumnus connect your account with a registration in UWB Alumni Club.',
						})}
					</Text>
					<Flex flexWrap="wrap" mt={3} gap={3}>
						<NavLinkButton
							variant="primary"
							to="/registration/alumnus/0"
							aria-label={localize({
								CZECH: 'Absolvent - Registrujte se zde',
								ENGLISH: 'Alumnus - Register here',
							})}
						>
							{localize({
								CZECH: 'Registrujte se zde',
								ENGLISH: 'Register here',
							})}
						</NavLinkButton>
						<ModalDialog
							aria-label={localize({
								CZECH: 'Absolvent - Více informací',
								ENGLISH: 'Alumnus - More infromation',
							})}
							label={localize({
								CZECH: 'Absolvent - Více informací',
								ENGLISH: 'Alumnus - More infromation',
							})}
							control={openModal => (
								<NavButton
									variant="primary"
									onClick={openModal}
									aria-label={localize({
										CZECH: 'Absolvent - Více informací',
										ENGLISH: 'Alumnus - More infromation',
									})}
								>
									{localize({
										CZECH: 'Více informací',
										ENGLISH: 'More infromation',
									})}
								</NavButton>
							)}
						>
							{closeModal => (
								<Paper>
									<Box px={4} py={3}>
										<Text fontWeight="bold" textAlign="center" fontSize={18}>
											{localize({
												CZECH: 'Absolvent - více informací',
												ENGLISH: 'Alumni - more infromation',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH: 'Registrujte se a vyplňte si svůj profil',
												ENGLISH: 'Register and fill in your profile',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Vlastní kariérní profil a životopis online můžete využívat i jako absolvent/ka do tří let po ukončení studia. K registraci v portálu UniJobs postačí, pokud jste zaregistrován v Klubu absolventů ZČU. Pokud jste již portál UniJobs využíval/a jako student/studentka, přes kontaktní email se Vám profil při registraci propojí. Vyplňte svůj profil co nejlépe včetně svých kariérních preferencí, usnadní se Vám vyhledávání relevantních nabídek.',
												ENGLISH:
													'You can use your career profile and CV online as an alumnus within three years after graduation. For registration at a portal UniJobs is enough if you are already registered in the UWB Alumni Club. If you have already used the UniJobs portal as a student, your profile will be connected during registration via contact e-mail. Fill in your profile as best you can, including your career preferences. It will make easier for you to find relevant offers.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH: 'Prohlížejte nabídky zaměstnavatelů',
												ENGLISH: 'View employers´ offers',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Zaměstnavatelé nabízejí v portálu UniJobs pozice přímo pro Vás, relevantní pro čerstvé absolventy bez dlouhé praxe. Můžete si vybrat vhodnou nabídku pro úspěšný start své profesní kariéry.',
												ENGLISH:
													'Employers at UniJobs portal offer positions directly for you, relevant for recent graduates without long-term experience. You can choose a suitable offer for a successful start of your professional career.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH:
													'Kontaktujte zaměstnavatele s relevantní nabídkou přímo pro Vás',
												ENGLISH:
													'Contact the employer with the relevant offer directly for you',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Portál UniJobs Vám automaticky doporučí odpovídající nabídky zaměstnavatelů na základě Vašich preferencí a dovedností. Máte k dispozici průběžné informace o tom, které požadavky v pracovních nabídkách splňujete.',
												ENGLISH:
													'The UniJobs portal will automatically recommend appropriate offers from employers based on your preferences and skills. Ongoing information about requirements you meet in job offers is available. ',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH: 'Využívejte efektivně svůj čas',
												ENGLISH: 'Use your time effectively',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Portál UniJobs Vám šetří čas, na vybranou nabídku můžete zareagovat jedním kliknutím! Pokud svůj profil kvalitně vyplníte, mohou Vás zaměstnavatele aktivně oslovit s nabídkou přesně pro Vás. Věnujte proto čas vyplňování svého profilu hned na začátku, abyste ho více ušetřili v budoucnosti.',
												ENGLISH:
													'The UniJobs portal saves your time, you can respond the selected offer with one click! If you fill in your profile well, employers can actively contact you with the offer exactly for you. Take your time to fill in your profile right at the beginning and save more time in the future.',
											})}
										</Text>
										<Text mt={4}>
											{localize({
												CZECH: 'Potřebujete poradit? Napište nám na e-mail: ',
												ENGLISH: 'Do you need an advice? Write us at e-mail: ',
											})}
											<HrefLink color="primary" href="mailto:career@rek.zcu.cz">
												career@rek.zcu.cz
											</HrefLink>
											.
										</Text>
										<Flex
											justifyContent="space-between"
											alignItems="center"
											mt={4}
										>
											<Button onClick={closeModal} variant="outlined">
												{localize({
													CZECH: 'Zpět',
													ENGLISH: 'Back',
												})}
											</Button>
											<NavLinkButton
												variant="primary"
												to="/registration/alumnus/0"
											>
												{localize({
													CZECH: 'Zaregistrovat se',
													ENGLISH: 'Register',
												})}
											</NavLinkButton>
										</Flex>
									</Box>
								</Paper>
							)}
						</ModalDialog>
					</Flex>
				</Box>
			</ResponsiveWrapper>

			<ResponsiveWrapper
				pt={[0, 2, 3, 4]}
				color="black"
				ref={companySectionRef}
				flexDirection={['column', 'row']}
			>
				<Box width={[1, 1 / 2]} mr={[0, 4]}>
					<TitleText
						width="fit-content"
						p={0}
						css={css`
							margin-left: 0;
							text-align: left;
						`}
					>
						{localize({
							CZECH: 'Pro zaměstnavatele',
							ENGLISH: 'Employer',
						})}
					</TitleText>
					<Text>
						{localize({
							CZECH:
								'Hledá Vaše firma zaměstnance z řad studentů či čerstvých absolventů (do třÍ let praxe)? Portál UniJobs je tu pro Vás. Nabízíme transparentní možnost, jak oslovit studenty všech fakult Západočeské univerzity v Plzni. Portál umožňuje:',
							ENGLISH:
								'Is your company looking for employees among the students or graduates (up to three years of experience)? UniJobs Portal is here for you. We offer the transparent possibility to address the students of all faculties/ departments of UWB. The Portal allows:',
						})}
					</Text>
					<UnorderedList>
						<ListItem>
							{localize({
								CZECH: 'vytvořit profil firmy a představit se tak studentům',
								ENGLISH:
									'To create a company profile and introduce yourself to students',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH:
									'inzerovat Vaše pracovní nabídky, nabídky praxí a stáží, témata závěrečných prací či jakoukoli jinou formu spolupráce firmy se studenty',
								ENGLISH:
									'To advertise your job offers, internship offers, and topics of final theses or any other form of cooperation between the company and students',
							})}
						</ListItem>
						<ListItem>
							{localize({
								CZECH: 'vyhledávat studenty dle zadaných kritérií',
								ENGLISH:
									'To search for students according to the specified criteria',
							})}
						</ListItem>
					</UnorderedList>
					<Flex mt={3} gap={3} flexWrap="wrap">
						<NavLinkButton
							variant="primary"
							to="/registration/company"
							aria-label={localize({
								CZECH: 'Zaměstnavatel - Registrujte se zde',
								ENGLISH: 'Employer - Register here',
							})}
						>
							{localize({
								CZECH: 'Registrujte se zde',
								ENGLISH: 'Register here',
							})}
						</NavLinkButton>
						<ModalDialog
							aria-label={localize({
								CZECH: 'Zaměstnavatel - Více informací',
								ENGLISH: 'Employer - More infromation',
							})}
							label={localize({
								CZECH: 'Zaměstnavatel - Více informací',
								ENGLISH: 'Employer - More infromation',
							})}
							control={openModal => (
								<NavButton
									variant="primary"
									onClick={openModal}
									aria-label={localize({
										CZECH: 'Zaměstnavatel - Více informací',
										ENGLISH: 'Employer - More infromation',
									})}
								>
									{localize({
										CZECH: 'Více informací',
										ENGLISH: 'More infromation',
									})}
								</NavButton>
							)}
						>
							{closeModal => (
								<Paper>
									<Box px={4} py={3}>
										<Text fontWeight="bold" textAlign="center" fontSize={18}>
											{localize({
												CZECH: 'Zaměstnavatel - více informací',
												ENGLISH: 'Employer - more infromation',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH:
													'Prezentace zaměstnavatele – Vytvořte profil Vaší firmy',
												ENGLISH:
													'Employer´s presentation – Create the profile of your company',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Na UniJobs získáte unikátní příležitost pro budování a posilování své značky zaměstnavatele vytvořením zaměstnavatelského profilu. UniJobs představuje nástroj efektivní online komunikace, pomocí kterého můžete prezentovat Vaši firemní kulturu, popsat průběh přijímacího procesu nebo vymezit své požadavky na uchazeče z řad studentů a čerstvých absolventů ZČU.',
												ENGLISH:
													'At UniJobs you will get a unique opportunity to build and strengthen your employer brand by creating an employer profile. UniJobs presents a tool of effective online communication through/ via which you can present your company culture, describe the acceptance process or define your requirements for applicants among the students and fresh graduates of the UWB.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH:
													'Základní profil – Spolupracujte se studenty na kvalifikační práci',
												ENGLISH:
													'Basic profile – Cooperate with students on qualification work',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Chcete spolupracovat se studenty při zpracování závěrečných kvalifikačních praxí? Základní profil Vám umožní nabízet studentům ZČU témata bakalářských a diplomových prací zdarma.',
												ENGLISH:
													'Do you want to cooperate with students during the elaboration of final qualification experiences? The basic profile will allow you to offer the UWB students the topics for bachelor´s and master´s theses for free.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH:
													'Inzertní profil – Inzerujte cíleně pro studenty/čerstvé absolventy',
												ENGLISH:
													'Advertising profile – Advertise directly for students/ fresh graduates',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Máte nabídky pracovních pozic, stáží, praxí pro studenty nebo absolventy? Na UniJobs můžete studentům a čerstvým absolventům ZČU cíleně nabídnout aktuální pracovní pozice, praxe/stáže nebo trainee programy.',
												ENGLISH:
													'Do you have job offers, internships, work experiences for students or graduates? At UniJobs you can directly offer current job positions, internships/ work experiences or trainee programs to students and fresh graduates of UWB.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH:
													'Databázový profil – Vyhledávejte uchazeče v databázi',
												ENGLISH:
													'Database profile – Search for candidates in the database',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Preferujete oslovení studentů/absolventů dle vlastního výběru? Vstupujte aktivně do databáze registrovaných studentů a čerstvých absolventů. UniJobs Vám umožní oslovit právě ty uchazeče, kteří splňují Vámi stanovené požadavky. Portál umožňuje filtrovat registrované studenty/absolventy, proto máte jistotu, že vyhledáte vhodného kandidáta.',
												ENGLISH:
													'Do you prefer to address students/ graduates according to your own choice? Enter the database of registered students and fresh graduates. UniJobs will allow you to address those candidates who fulfill your requirements. The Portal allows to filter registered student/ graduates, so you can be sure that you will find an appropriate candidate.',
											})}
										</Text>
										<Text
											fontWeight="bold"
											color="primary"
											fontSize="lg"
											mt={3}
										>
											{localize({
												CZECH:
													'Kompletní profil – Zkombinujte výhody inzerce a databáze',
												ENGLISH:
													'Complete profile – Combine the benefits of advertising and database',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'Pokud potřebujete nabídkou oslovit co nejvíce zájemců a zároveň cíleně hledat mezi vhodnými uchazeči, zvolte si Kompletní profil. Portál UniJobs v reálném čase porovnává Vaše požadavky v nabídkách a doporučí Vám kandidáty, kteří odpovídají Vašim kritériím. Relevantní nabídky následně portál doporučuje vhodným kandidátům, kteří Vás naopak mohou sami kontaktovat. Kompletní profil Vám umožní využívat výhody online portálu v plném rozsahu.',
												ENGLISH:
													'If you need to address as many interested persons as possible with your offer and search directly among the suitable candidates at the same time, choose the Complete profile. The UniJobs Portal compares your requirements in offers in real-time and recommends to you the candidates who meet your criteria. Relevant offers are recommended to suitable candidates via our Portal. Those candidates can contact you by themselves. The Complete profile will allow you to take all advantages of the online Portal in the full range.',
											})}
										</Text>
										<Text mt={4} fontWeight="bold">
											{localize({
												CZECH:
													'Do 31. 8. 2021 nabízíme možnost vyzkoušení kariérního portálu UniJobs zdarma. Neváhejte a zaregistrujte se.',
												ENGLISH:
													'We offer the opportunity to try the UniJobs career portal for free until 31 August 2021. Do not hesitate and register.',
											})}
										</Text>
										<Text mt={4}>
											{localize({
												CZECH:
													'Další informace k jednotlivým profilům včetně nabídky po ukončení zkušebního provozu naleznete v Ceníku, který najdete na úvodní stránce kariérního portálu UniJobs.',
												ENGLISH:
													'For further information on individual profiles, including the offer after the end of the setting up operation, see the Price List, which you can find on the front page of career portal UniJobs.',
											})}
										</Text>
										<Text>
											{localize({
												CZECH:
													'V případě dotazů nás neváhejte kontaktovat na e-mailu: ',
												ENGLISH:
													'If you have any questions, you can contact us via e-mail: ',
											})}
											<HrefLink color="primary" href="mailto:career@rek.zcu.cz">
												career@rek.zcu.cz
											</HrefLink>
											.
										</Text>
										<Flex
											justifyContent="space-between"
											alignItems="center"
											mt={4}
										>
											<Button onClick={closeModal} variant="outlined">
												{localize({
													CZECH: 'Zpět',
													ENGLISH: 'Back',
												})}
											</Button>
											<NavLinkButton
												variant="primary"
												to="/registration/company"
											>
												{localize({
													CZECH: 'Zaregistrovat se',
													ENGLISH: 'Register',
												})}
											</NavLinkButton>
										</Flex>
									</Box>
								</Paper>
							)}
						</ModalDialog>
						{/* temporary disable price list */}
						{/* <NavHrefButton href="unijobs_cenik.pdf" download variant="outlined">
							Ceník
						</NavHrefButton> */}
					</Flex>
				</Box>
				<Flex
					width={[1, 1 / 2]}
					ml={0}
					mt={4}
					height={300}
					css={css`
						background-image: url(${homeCompany});
						background-size: cover;
						background-repeat: no-repeat;
						background-position: center;
					`}
				/>
			</ResponsiveWrapper>

			<ResponsiveWrapper>
				<Flex
					justifyContent="space-between"
					flexDirection={['column', 'row']}
					bg="primaryLight"
					px={4}
					pt={[3, 0]}
					pb={[4, 0]}
					alignItems="center"
				>
					<TitleText>
						{localize({
							CZECH: 'Potřebujete poradit?',
							ENGLISH: 'Do you need advice?',
						})}
					</TitleText>
					<NavHrefButton
						variant="primary"
						height={50}
						href="mailto:career@rek.zcu.cz"
					>
						{localize({
							CZECH: 'Napište nám',
							ENGLISH: 'Contact us',
						})}
					</NavHrefButton>
				</Flex>
				<BlueBackground imageSrc={topViewBlue} height={277}>
					<Flex alignItems="center" flexDirection="column">
						<Box>
							<Text fontWeight="bold">
								Informační a poradenské centrum Západočeské univerzity v Plzni
							</Text>
							<Text>Univerzitní 20, Plzeň</Text>
						</Box>
						<Flex flexDirection={['column', 'row']}>
							<Grid gridTemplateColumns="1fr 1fr" p={[0, 3]}>
								<Text>
									{localize({
										CZECH: 'Telefon:',
										ENGLISH: 'Phone:',
									})}
								</Text>
								<Text fontWeight="bold">+420 377 631 220</Text>
								<span>{/**empty grid cell */}</span>
								<Text fontWeight="bold">+420 377 631 221</Text>
								<Text>E-mail:</Text>
								<Text fontWeight="bold">career@rek.zcu.cz</Text>
							</Grid>
							<Grid gridTemplateColumns="2fr 1fr" p={[0, 3]} ml={[0, 5]}>
								<Text>IČ:</Text>
								<Text fontWeight="bold"> 49777513</Text>
								<Text>DIČ:</Text>
								<Text fontWeight="bold">CZ49777513</Text>
								<Text>ID datové schránky:</Text>
								<Text fontWeight="bold">zqfj9hj</Text>
							</Grid>
						</Flex>
					</Flex>
				</BlueBackground>
			</ResponsiveWrapper>

			<ScrollToTopButton />
		</Wrapper>
	);
};

export default PlzenHomepage;
